import { inject, Injectable, NgZone } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';

/**
 * Preloads only the routes that ask for it, and only while the browser is idle.
 *
 * R19. The router had no preloading at all, so the first navigation into any lazy route paid for
 * its chunk. `PreloadAllModules` is the one-line answer and the measurements argue against it: the
 * production build has 115 lazy chunks totalling **1.30 MB gzip** (4.83 MB raw) on top of an 818 kB
 * initial transfer, and 265 kB of that is the Stripe/Mixpanel checkout chunk that most sessions
 * never touch. Doubling a session's download to save a few hundred milliseconds on screens the user
 * may not open is not a trade worth making for them.
 *
 * So a route opts in with `data: { preload: true }` — the developer chose dashboard, transactions,
 * accounts and insights — and even then the fetch waits for
 * `requestIdleCallback`: at startup this app is busy loading and processing containers, and a
 * preload that competes with that makes the screen the user is actually looking at slower.
 *
 * `runOutsideAngular` matters here — an idle callback that resolves inside the zone would schedule
 * change detection for work nobody is waiting on.
 *
 * A route may also name a follow-up in `data: { warmUp: () => Promise<unknown> }` — something its
 * screen fetches on first render anyway (the chart library, see `warmUpChartLibrary`). It runs once
 * the route's chunk has arrived, so the idle budget goes to the route first, and its failure is
 * reported the same way and never reaches the router.
 */
@Injectable({ providedIn: 'root' })
export class IdleMarkedRoutesPreloadingStrategy implements PreloadingStrategy {
  private readonly zone = inject(NgZone);

  /** Two seconds, for browsers without `requestIdleCallback` — Safari before 17. */
  private static readonly FALLBACK_DELAY_MS = 2000;

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload']) return EMPTY;

    return new Observable((subscriber) => {
      let cancelled = false;
      let cancel: () => void = () => undefined;

      this.zone.runOutsideAngular(() => {
        // A preload is a courtesy: if it fails, the navigation that needs the chunk asks again.
        // Saying so beats a silent gap in the network log — and a failure here must never reach
        // the router, which would turn a missed optimisation into a broken startup.
        const giveUp = (err: unknown) => {
          console.warn(
            `Could not preload the "${route.path}" route — it will load on navigation instead:`,
            err
          );
          subscriber.complete();
        };

        const warmUp = () => {
          const follow = route.data?.['warmUp'];
          if (typeof follow !== 'function') return;
          try {
            Promise.resolve(follow()).catch((err: unknown) =>
              console.warn(
                `Could not warm up for the "${route.path}" route — its screen will fetch it on first render instead:`,
                err
              )
            );
          } catch (err) {
            console.warn(
              `Could not warm up for the "${route.path}" route — its screen will fetch it on first render instead:`,
              err
            );
          }
        };

        const start = () => {
          if (cancelled) return;

          try {
            load().subscribe({
              next: (v) => subscriber.next(v),
              error: giveUp,
              complete: () => {
                warmUp();
                subscriber.complete();
              },
            });
          } catch (err) {
            // `load()` itself throwing, rather than the stream it returns.
            giveUp(err);
          }
        };

        const idle = (
          window as unknown as {
            requestIdleCallback?: (cb: () => void) => number;
            cancelIdleCallback?: (handle: number) => void;
          }
        ).requestIdleCallback;

        if (idle) {
          const handle = idle(start);
          cancel = () =>
            (
              window as unknown as {
                cancelIdleCallback?: (h: number) => void;
              }
            ).cancelIdleCallback?.(handle);
        } else {
          const handle = setTimeout(
            start,
            IdleMarkedRoutesPreloadingStrategy.FALLBACK_DELAY_MS
          );
          cancel = () => clearTimeout(handle);
        }
      });

      return () => {
        cancelled = true;
        cancel();
      };
    });
  }
}
